import {
	useState,
	useLayoutEffect,
	useCallback,
	useEffect,
} from "preact/compat";
import { Link, useLocation, NavLink } from "react-router";
import { clsx } from "clsx";
import { useBranding } from "@brickme/d2c-branding/src/browser/context.tsx";
import PoweredByBrickMe from "@brickme/frontend-common/src/ui/powered-by-brick-me.tsx";
import { useWindowEvent } from "~/hooks/ui.ts";
import { VectorIcon } from "~/components/graphics.tsx";
import {
	useAuthenticationState,
	getSignedInInitials,
	isSignedInAuthenticationResult,
	isLoadingAuthenticationResult,
	isSignedOutAuthenticationResult,
} from "~/hooks/auth-state.tsx";
import { useWebsiteUrl } from "~/contexts/theme.tsx";
// TODO: Remove react-use - not many usages left

function Header() {
	// scrolled state
	const [isScrolled, setScrolled] = useState(false);
	const updateScroll = useCallback(() => {
		setScrolled(window.scrollY > 50);
	}, []);
	useWindowEvent("scroll", updateScroll);
	useEffect(() => {
		updateScroll();
	}, [updateScroll]);

	// css requires access to scroll width
	useWindowEvent(
		"resize",
		useCallback(() => {
			const scrollbarWidth =
				window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.setProperty(
				"--scrollbar-width",
				`${scrollbarWidth}px`,
			);
		}, []),
	);

	// Menu toggle
	const [isMenuOpen, setMenuOpen] = useState(false);
	useEffect(() => {
		document.body.classList.toggle("no-scroll", isMenuOpen);
	}, [isMenuOpen]);
	const onMenuOpenClick = () => {
		setMenuOpen((p) => !p);
	};
	const { pathname } = useLocation();
	useLayoutEffect(() => {
		setMenuOpen(false);
	}, [pathname]);

	const websiteUrl = useWebsiteUrl();
	const authResult = useAuthenticationState();
	const branding = useBranding();

	return (
		<div className="r-main-header-outer js-r-main-header-outer">
			<header
				className={clsx("r-main-header js-r-main-header", {
					"is-scrolled": isScrolled,
				})}
			>
				<div className="r-main-header__container container">
					<div className="r-main-header__content">
						<span className="r-main-header__menu-opener">
							<button
								type="button"
								className={clsx("r-menu-opener", { active: isMenuOpen })}
								onClick={onMenuOpenClick}
							>
								<span className="r-menu-opener__inner">
									<span className="r-menu-opener__lines">
										<span className="r-menu-opener__lines-item" />
									</span>
								</span>
							</button>
						</span>

						<div className="r-main-header__logo">
							<Link
								to="/"
								className="r-main-header__logo-link"
								aria-label={`Go To The ${branding.companyName} Homepage`}
							>
								<div
									className={clsx(
										"r-main-header__logo-img logo-animated js-r-main-header__logo-img",
									)}
								>
									<img src={branding.logoSquareSvg} alt="" />
									{branding.showPoweredBy && <PoweredByBrickMe />}
								</div>
							</Link>
						</div>

						<nav
							className={clsx("r-main-header__menu r-main-menu", {
								active: isMenuOpen,
							})}
						>
							<div className="r-main-menu__inner">
								<ul
									className={clsx("r-main-menu__list", {
										active: isMenuOpen,
									})}
								>
									<li className="r-main-menu__item">
										<a href={websiteUrl} className="r-main-menu__link">
											&lt; Back to Homepage
										</a>
									</li>
									<li className="r-main-menu__item">
										<a
											href={`${websiteUrl}/account`}
											className="r-main-menu__link"
										>
											&lt; Back to My Orders
										</a>
									</li>
								</ul>
								<div className="r-main-menu__mobile-box">
									{isSignedInAuthenticationResult(authResult) && (
										<NavLink
											to="/profile/"
											className="r-main-menu__mobile-item initials-btn"
										>
											{getSignedInInitials(authResult)}
										</NavLink>
									)}
									{isLoadingAuthenticationResult(authResult) && (
										<div className="r-main-menu__mobile-item icon-link">
											<VectorIcon iconName="user" className="icon-link__icon" />
											<span className="icon-link__text">Login</span>
										</div>
									)}
									{isSignedOutAuthenticationResult(authResult) && (
										<NavLink
											to="/login/"
											className="r-main-menu__mobile-item icon-link"
										>
											<VectorIcon iconName="user" className="icon-link__icon" />
											<span className="icon-link__text">Login</span>
										</NavLink>
									)}
								</div>
							</div>
						</nav>
						<aside className="r-main-header__aside">
							{isSignedInAuthenticationResult(authResult) && (
								<NavLink
									to="/profile/"
									className="icon-btn initials-btn"
									aria-label="Login"
									title="Login"
								>
									{getSignedInInitials(authResult)}
								</NavLink>
							)}
							{isLoadingAuthenticationResult(authResult) && (
								<div className="r-main-header__login icon-btn">
									<VectorIcon iconName="user" className="icon-btn__icon" />
								</div>
							)}
							{isSignedOutAuthenticationResult(authResult) && (
								<NavLink
									to="/login/"
									className="r-main-header__login icon-btn"
									aria-label="Login"
									title="Login"
								>
									<VectorIcon iconName="user" className="icon-btn__icon" />
								</NavLink>
							)}
						</aside>
					</div>
				</div>
			</header>
		</div>
	);
}

export default Header;
